import { parseTenantSlug } from '../../../core/tenancy';

import type { IconName } from '@shared/components/icon/icon-registry';
import type { FeatureFlag } from '@shared/services/feature-flags';

/**
 * Available icon types for navigation elements — the navigation-relevant subset
 * of the shared icon registry. Derived via `Extract` so every nav glyph is
 * guaranteed to exist in `ICON_PRIMITIVES` (a typo is a compile error).
 */
export type NavIconType = Extract<
  IconName,
  | 'folder'
  | 'book-open'
  | 'calendar'
  | 'calendar-check'
  | 'id-card'
  | 'layout-dashboard'
  | 'receipt'
  | 'shopping-cart'
  | 'users'
  | 'package'
  | 'settings'
  | 'chart-column'
  | 'chevron-down'
  | 'panel-left-close'
  | 'panel-left-open'
  | 'blocks'
  | 'sliders'
>;

/**
 * Single navigation item (leaf node).
 * Used as children within sections or groups.
 *
 * @example
 * ```typescript
 * const item: NavigationItem = {
 *   id: 'events',
 *   label: 'Events',
 *   icon: 'calendar',
 *   route: '/events'
 * };
 * ```
 */
export interface NavigationItem {
  /** Unique identifier */
  readonly id: string;
  /** Display label */
  readonly label: string;
  /** Icon identifier (matches NavIconType) */
  readonly icon: NavIconType;
  /** Router path (e.g., '/events') */
  readonly route: string;
  /**
   * Optional feature flag gating this item. When set and the flag is disabled,
   * {@link filterNavigationByRights} drops the item. Absent = always shown
   * (subject to rights).
   */
  readonly featureFlag?: FeatureFlag;
}

/**
 * Root-level navigation item (direct link without children).
 * Renders as a standalone navigation link at the root level.
 * Literal discriminant: `kind: 'item'`.
 *
 * @example
 * ```typescript
 * const rootItem: NavigationRootItem = {
 *   kind: 'item',
 *   id: 'dashboard',
 *   label: 'Dashboard',
 *   icon: 'calendar',
 *   route: '/dashboard'
 * };
 * ```
 */
export interface NavigationRootItem {
  /** Literal discriminant for the {@link NavigationEntry} union. */
  readonly kind: 'item';
  /** Unique identifier */
  readonly id: string;
  /** Display label */
  readonly label: string;
  /** Icon identifier (matches NavIconType) */
  readonly icon: NavIconType;
  /** Router path (e.g., '/dashboard') */
  readonly route: string;
  /**
   * Optional feature flag gating this item. When set and the flag is disabled,
   * {@link filterNavigationByRights} drops the item. Absent = always shown
   * (subject to rights).
   */
  readonly featureFlag?: FeatureFlag;
}

/**
 * Navigation section (group of items).
 *
 * @example
 * ```typescript
 * const section: NavigationSection = {
 *   kind: 'section',
 *   id: 'catalog',
 *   label: 'Catalog',
 *   icon: 'folder',
 *   children: [{ id: 'events', label: 'Events', icon: 'calendar', route: '/events' }]
 * };
 * ```
 */
export interface NavigationSection {
  /** Literal discriminant for the {@link NavigationEntry} union. */
  readonly kind: 'section';
  /** Unique identifier */
  readonly id: string;
  /** Section header label */
  readonly label: string;
  /** Section icon */
  readonly icon: NavIconType;
  /** Child navigation items */
  readonly children: readonly NavigationItem[];
}

/**
 * Navigation entry - discriminated union of all root-level types.
 * Supports both direct items and collapsible groups/sections.
 * Discriminated on the literal `kind` field: `'item'` vs `'section'`.
 */
export type NavigationEntry = NavigationRootItem | NavigationSection;

/**
 * Complete navigation configuration.
 * Now supports mixed entries: both root items and groups/sections.
 */
export type NavigationConfig = readonly NavigationEntry[];

/**
 * Type guard: check if entry is a root item, via the `kind` discriminant.
 *
 * @param entry - Navigation entry to check
 * @returns True if entry is a NavigationRootItem
 *
 * @example
 * ```typescript
 * if (isRootItem(entry)) {
 *   // TypeScript knows entry has `route` and no `children`
 *   console.log(entry.route);
 * }
 * ```
 */
export function isRootItem(entry: NavigationEntry): entry is NavigationRootItem {
  return entry.kind === 'item';
}

/**
 * Type guard: check if entry is a section (group), via the `kind` discriminant.
 *
 * @param entry - Navigation entry to check
 * @returns True if entry is a NavigationSection
 *
 * @example
 * ```typescript
 * if (hasChildren(entry)) {
 *   // TypeScript knows entry has `children`
 *   entry.children.forEach(...);
 * }
 * ```
 */
export function hasChildren(entry: NavigationEntry): entry is NavigationSection {
  return entry.kind === 'section';
}

/**
 * Strip query string and fragment from a URL, returning the pathname portion.
 */
function stripQueryAndFragment(url: string): string {
  return (url.split('?')[0] ?? '').split('#')[0] ?? '';
}

/**
 * Check whether a route matches the current URL.
 * An item route matches if it equals the URL pathname or is a prefix followed by '/'.
 *
 * @param itemRoute - Route path from a NavigationItem / NavigationRootItem (e.g. '/events')
 * @param currentUrl - Current router URL (may include query string / fragment)
 */
export function isRouteActive(itemRoute: string, currentUrl: string): boolean {
  if (!itemRoute || !currentUrl) return false;
  const pathname = stripQueryAndFragment(currentUrl);
  return pathname === itemRoute || pathname.startsWith(itemRoute + '/');
}

/**
 * Strip the query string, fragment, and leading `/{tenant}` segment from a
 * router URL, returning a tenant-relative pathname that matches the
 * tenant-agnostic navigation config (whose routes are absolute, e.g. `/events`).
 *
 * When the first path segment is a valid tenant slug it is removed; otherwise
 * the pathname is returned unchanged. An empty/blank URL yields `''`.
 *
 * @param url - Current router URL (may include `/{tenant}` prefix, query, fragment)
 */
export function stripTenantSegment(url: string): string {
  const pathname = stripQueryAndFragment(url);
  const segments = pathname.split('/').filter(Boolean);
  return parseTenantSlug(segments[0]) !== undefined
    ? `/${segments.slice(1).join('/')}`
    : pathname;
}

/**
 * Find the ids of all sections/groups containing a child whose route matches
 * the current URL. Used to auto-expand sections on navigation.
 *
 * Pure function — takes the navigation configuration and url as input.
 */
export function findExpandableSectionIds(
  entries: readonly NavigationEntry[],
  url: string
): readonly string[] {
  const ids: string[] = [];
  for (const entry of entries) {
    if (isRootItem(entry)) continue;
    const hasActiveChild = entry.children.some((item) =>
      isRouteActive(item.route, url)
    );
    if (hasActiveChild) {
      ids.push(entry.id);
    }
  }
  return ids;
}

/**
 * Filter navigation entries by an access predicate (route path → allowed) and a
 * feature-flag predicate (flag → enabled).
 *
 * An entry is kept iff its route is allowed AND its `featureFlag` (when set) is
 * enabled. Root items are kept individually; sections are kept iff at least one
 * child survives, with dropped children removed. Pure function — both the
 * rights/authorization lookup and the flag lookup are supplied as predicates, so
 * this module gains no dependency on the authorization or feature-flag layers.
 */
export function filterNavigationByRights(
  entries: readonly NavigationEntry[],
  allowedPath: (path: string) => boolean,
  flagEnabled: (flag: FeatureFlag) => boolean
): readonly NavigationEntry[] {
  const flagOk = (featureFlag: FeatureFlag | undefined): boolean =>
    featureFlag === undefined || flagEnabled(featureFlag);
  return entries.flatMap((entry): readonly NavigationEntry[] => {
    if (isRootItem(entry)) {
      return allowedPath(entry.route) && flagOk(entry.featureFlag) ? [entry] : [];
    }
    const children = entry.children.filter(
      (child) => allowedPath(child.route) && flagOk(child.featureFlag)
    );
    return children.length > 0 ? [{ ...entry, children }] : [];
  });
}

/**
 * Every dev-only navigation entry, in render order. Kept deliberately OUT of
 * {@link DEFAULT_NAVIGATION} and out of {@link filterNavigationByRights}: these
 * are reserved TOP-LEVEL routes (`/components`, `/admin/feature-flags`), not
 * tenant-scoped business sections, so they render through
 * `NavDevItemComponent` (plain `[routerLink]`, no tenant prefix) pinned to the
 * bottom of the sidebar.
 *
 * Visibility is resolved by {@link resolveDevNavigation}, not here — see its
 * doc for which gate each entry carries.
 */
export const DEV_NAVIGATION: readonly NavigationRootItem[] = [
  {
    kind: 'item',
    id: 'components',
    label: 'navigation.components',
    icon: 'blocks',
    route: '/components',
  },
  {
    kind: 'item',
    id: 'feature-flag-admin',
    label: 'navigation.featureFlagAdmin',
    icon: 'sliders',
    route: '/admin/feature-flags',
  },
];

/** The gates {@link resolveDevNavigation} evaluates. */
export interface DevNavigationAccess {
  /** The `showComponentsGallery` environment flag — true on dev/staging builds. */
  readonly showGallery: boolean;
  /** Whether the current user holds the `FeatureFlagAdmin` section right. */
  readonly canAdminFeatureFlags: boolean;
}

/**
 * Resolve the visible dev navigation entries.
 *
 * Pure — every gate is passed in, so this module gains no dependency on the
 * config, authorization, or feature-flag layers (same discipline as
 * {@link filterNavigationByRights}, which takes predicates).
 *
 * Gates, per entry:
 * - **components gallery** — `showGallery` alone. A build-time dev artifact with
 *   no rights dimension.
 * - **feature-flag admin** — `showGallery` **and** `canAdminFeatureFlags`. The
 *   right is what keeps a staff-only console off every other user's sidebar (the
 *   route is independently gated by `sectionAuthorizationGuard`, so a stale entry
 *   could only ever land on `/access-denied` — but showing it would still be a
 *   lie). `showGallery` rides along **deliberately**: the console is wired to an
 *   in-memory mock in both `apiMode` branches until the Manager endpoints exist,
 *   so it must not appear on real production, and `showGallery` is already
 *   defined as "true on dev/staging, false on real production". Split it into its
 *   own flag when the HTTP adapter lands
 *   (`claudedocs/feature-flag-admin-console-plan.md` §6.1).
 */
export function resolveDevNavigation(
  access: DevNavigationAccess,
): readonly NavigationRootItem[] {
  if (!access.showGallery) {
    return [];
  }
  return DEV_NAVIGATION.filter(
    (entry) => entry.id !== 'feature-flag-admin' || access.canAdminFeatureFlags,
  );
}

/**
 * Default navigation configuration.
 * Demonstrates mixed root items and groups.
 */
export const DEFAULT_NAVIGATION: NavigationConfig = [
  {
    kind: 'item',
    id: 'dashboard',
    label: 'navigation.dashboard',
    icon: 'layout-dashboard',
    route: '/dashboard',
  },
  {
    kind: 'item',
    id: 'quick-sale',
    label: 'navigation.quickSale',
    icon: 'shopping-cart',
    route: '/sales',
  },
  {
    kind: 'item',
    id: 'orders',
    label: 'navigation.orders',
    icon: 'receipt',
    route: '/orders',
  },
  {
    kind: 'item',
    id: 'customers',
    label: 'navigation.customers',
    icon: 'users',
    route: '/customers',
  },
  {
    kind: 'item',
    id: 'sales-launch',
    label: 'navigation.salesLaunch',
    icon: 'calendar-check',
    route: '/mise-en-vente',
  },
  {
    kind: 'section',
    id: 'catalog',
    label: 'navigation.catalog',
    icon: 'book-open',
    children: [
      {
        id: 'events',
        label: 'navigation.events',
        icon: 'calendar',
        route: '/events',
      },
      {
        id: 'subscriptions',
        label: 'navigation.subscriptions',
        icon: 'id-card',
        route: '/subscriptions',
      },
      {
        id: 'events-parameters',
        label: 'navigation.resale',
        icon: 'calendar',
        route: '/catalog/events/parameters',
      },
      {
        id: 'products',
        label: 'navigation.products',
        icon: 'package',
        route: '/catalog/products',
      },
    ],
  },
  {
    kind: 'item',
    id: 'reports',
    label: 'navigation.reports',
    icon: 'chart-column',
    route: '/reports',
  },
  {
    kind: 'section',
    id: 'settings',
    label: 'navigation.settings',
    icon: 'settings',
    children: [
      {
        id: 'settings-mockup',
        label: 'navigation.mockup',
        icon: 'id-card',
        route: '/settings/mockup',
      },
      {
        id: 'settings-channels',
        label: 'navigation.channels',
        icon: 'folder',
        route: '/settings/channels',
      },
      {
        id: 'settings-resale-configuration',
        label: 'navigation.resaleConfiguration',
        icon: 'receipt',
        route: '/settings/resale-configuration',
      },
    ],
  },
];
